import { useState } from 'react';
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import { 
  FaPlus, 
  FaEdit, 
  FaSave, 
  FaTimes,
  FaCog
} from 'react-icons/fa';
import { 
  createProjectPhase, 
  updateProjectPhase
} from '../../api/projects';

interface PhaseManagerProps {
  token: string | undefined;
  projectId: string;
  phases: ProjectPhaseInterface[];
  onClose?: () => void;
  onPhasesUpdate?: () => void;
  isModal?: boolean;
}

export const PhaseManager = ({ 
  token, 
  projectId, 
  phases, 
  onClose, 
  onPhasesUpdate, 
  isModal = true 
}: PhaseManagerProps) => {
  const { keycloak } = useKeycloak();
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [newPhaseName, setNewPhaseName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Helper function to handle authentication issues
  const handleAuthError = () => {
    toast.error('Session expired. Please login again.');
    keycloak.logout({
      redirectUri: window.location.origin + '/login'
    });
  };

  // Create new phase
  const handleCreate = async () => {
    if (!token) {
      handleAuthError();
      return;
    }

    if (!newPhaseName.trim()) {
      toast.error('Phase name is required');
      return;
    }

    try {
      setLoading(true);
      const newPhase = {
        id: crypto.randomUUID(),
        project_id: projectId,
        name: newPhaseName.trim(),
        status: 'NOT_STARTED' as const
      };

      await createProjectPhase(token, projectId, newPhase);
      setNewPhaseName('');
      setIsCreating(false);
      toast.success('Phase created successfully');
      
      // Trigger parent update
      if (onPhasesUpdate) {
        await onPhasesUpdate();
      }
    } catch (error) {
      toast.error('Failed to create phase');
      console.error('Error creating phase:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update phase
  const handleUpdate = async (phaseId: string, name: string) => {
    if (!token) {
      handleAuthError();
      return;
    }

    if (!name.trim()) {
      toast.error('Phase name is required');
      return;
    }

    try {
      setLoading(true);
      const phase = phases.find(p => p.id === phaseId);
      if (!phase) {
        toast.error('Phase not found');
        return;
      }

      const updatedPhase = {
        ...phase,
        name: name.trim()
      };

      await updateProjectPhase(token, projectId, phaseId, updatedPhase);
      setEditingId(null);
      setEditingName('');
      toast.success('Phase updated successfully');
      
      // Trigger parent update
      if (onPhasesUpdate) {
        await onPhasesUpdate();
      }
    } catch (error) {
      toast.error('Failed to update phase');
      console.error('Error updating phase:', error);
    } finally {
      setLoading(false);
    }
  };



  const startEditing = (phase: ProjectPhaseInterface) => {
    setEditingId(phase.id);
    setEditingName(phase.name);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName('');
  };

  const content = (
    <div className="bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-[#eaeaea]">
        <div className="flex items-center space-x-3">
          <FaCog className="w-5 h-5 text-[#888]" />
          <h2 className="text-xl font-semibold font-[system-ui,-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif]">
            Manage Phases
          </h2>
        </div>
        {isModal && onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#fafafa] rounded-md transition-colors"
          >
            <FaTimes className="w-4 h-4 text-[#888]" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Create New Phase */}
        <div className="mb-6">
          {!isCreating ? (
            <button
              onClick={() => setIsCreating(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-[#171717] text-white rounded-md hover:bg-[#171717] transition-colors"
            >
              <FaPlus className="w-4 h-4" />
              <span>Add New Phase</span>
            </button>
          ) : (
            <div className="flex items-center space-x-3 p-4 border border-[#eaeaea] rounded-md bg-[#f5f5f5]">
              <input
                type="text"
                value={newPhaseName}
                onChange={(e) => setNewPhaseName(e.target.value)}
                placeholder="Enter phase name..."
                className="flex-1 px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreate();
                  } else if (e.key === 'Escape') {
                    setIsCreating(false);
                    setNewPhaseName('');
                  }
                }}
              />
              <button
                onClick={handleCreate}
                disabled={!newPhaseName.trim() || loading}
                className="px-3 py-2 bg-[#171717] text-white rounded-md hover:bg-[#333] disabled:bg-[#ddd] disabled:cursor-not-allowed transition-colors"
              >
                <FaSave className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setIsCreating(false);
                  setNewPhaseName('');
                }}
                className="px-3 py-2 bg-[#171717] text-white rounded-md hover:bg-[#555] transition-colors"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Phases List */}
        {loading && !isCreating ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#888]"></div>
            <span className="ml-3 text-[#555]">Loading phases...</span>
          </div>
        ) : phases.length === 0 ? (
          <div className="text-center py-8 text-[#888]">
            <FaCog className="w-12 h-12 mx-auto mb-4 text-[#ccc]" />
            <p>No phases found</p>
            <p className="text-sm">Create your first phase to get started</p>
          </div>
        ) : (
          <div className="space-y-2">
            {phases.map((phase) => (
              <div
                key={phase.id}
                className="flex items-center justify-between p-4 border border-[#eaeaea] rounded-lg hover:bg-[#fafafa] transition-colors"
              >
                {editingId === phase.id ? (
                  <div className="flex items-center space-x-3 flex-1">
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      className="flex-1 px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                      autoFocus
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleUpdate(phase.id, editingName);
                        } else if (e.key === 'Escape') {
                          cancelEditing();
                        }
                      }}
                    />
                    <button
                      onClick={() => handleUpdate(phase.id, editingName)}
                      disabled={!editingName.trim() || loading}
                      className="px-3 py-2 bg-[#171717] text-white rounded-md hover:bg-[#333] disabled:bg-[#ddd] disabled:cursor-not-allowed transition-colors"
                    >
                      <FaSave className="w-4 h-4" />
                    </button>
                    <button
                      onClick={cancelEditing}
                      className="px-3 py-2 bg-[#171717] text-white rounded-md hover:bg-[#555] transition-colors"
                    >
                      <FaTimes className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center space-x-3">
                      <span className="font-medium text-[#171717]">{phase.name}</span>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        phase.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                        phase.status === 'IN_PROGRESS' ? 'bg-[#f5f5f5] text-[#171717]' :
                        'bg-[#f5f5f5] text-[#444]'
                      }`}>
                        {phase.status?.replace('_', ' ') || 'Not Started'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => startEditing(phase)}
                        className="p-2 text-[#888] hover:bg-[#f5f5f5] rounded-md transition-colors"
                        title="Edit"
                      >
                        <FaEdit className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
          {content}
        </div>
      </div>
    );
  }

  return content;
}; 